import React, { useCallback, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";

import store from "./redux/store";
import Login from "./components/Login";
import Home from "./components/Home";
import EdgeList from "./pages/EdgeList";
import EdgeExplorer from "./pages/EdgeExplorer";
import EdgeDetail from "./pages/EdgeDetail";
import DeviceLookup from "./pages/DeviceLookup";
import Marketplace from "./pages/Marketplace";
import MarketplaceHistory from "./pages/MarketplaceHistory";
import TenantList from "./pages/TenantList";
import AlarmList from "./pages/AlarmList";
import VpnUsers from "./pages/VpnUsers";
import RcaAssistant from "./pages/RcaAssistant";
import OtaManagement from "./pages/OtaManagement";
import SecurityCenter from "./pages/SecurityCenter";
import ProductGate from "./components/ProductGate";
import MachineSbom from "./pages/MachineSbom";
import SshAccess from "./pages/SshAccess";
import AiSettings from "./pages/AiSettings";
import LicenseManagement from "./pages/LicenseManagement";
import EdgeTransfers from "./pages/EdgeTransfers";
import MainLayout from "./components/Layout/MainLayout";
import { base_name } from "./config";
import Forgot from "./pages/Forgot";
import LanguageSwitcher from "./components/Layout/LanguageSwitcher";
import ResetPassword from "./pages/Reset";
import FirstLoginPassword from "./pages/FirstLoginPassword";
import SessionExpiryDialog from "./components/SessionExpiryDialog";
import { useSessionTimers } from "./hooks/useSessionTimers";
import { refreshAccess, logoutApi,getMe } from "./api/authApi";
import {
  setAccess,
  setMe,
  consumeExtension,
  logout as logoutAction,
} from "./redux/sessionSlice";
import Profile from "./components/profile";
import TwoFactorSettings from "./components/TwoFactorSettings";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";

/**
 * AppShell:
 * - Uygulama açılışında cookie varsa refresh yapıp access token üretir
 * - access token gelince /me çekip redux'a yazar
 * - Session popup + expire timer burada yönetilir
 */

function AppShell() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const accessToken = useSelector((s) => s.session.accessToken);
  const accessExp = useSelector((s) => s.session.accessExp);
  const me = useSelector((s) => s.session.me);
  const status = useSelector((s) => s.session.status);
  const extensionsLeft = useSelector((s) => s.session.extensionsLeft);

  const [booting, setBooting] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const canExtend = status === "authenticated" && extensionsLeft > 0;
  const WARNING_BEFORE_MS = 60 * 1000;

  const doLogout = useCallback(async () => {
    try {
      await logoutApi(); // refresh cookie silinsin
    } catch (e) {
      // backend logout patlasa bile frontend logout yapacağız
    } finally {
      localStorage.removeItem("user_info");
      dispatch(logoutAction());
      setDialogOpen(false);
      navigate("/", { replace: true });
    }
  }, [dispatch, navigate]);

  /**
   * Sayfa ilk açıldığında 1 kere çalışır.
   * Cookie varsa /auth/refresh çağırıp access token alır.
   * Not: dependency boş bırakıyoruz -> 1 kere çalışsın diye.
   */
  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        // accessToken zaten varsa refresh yapmaya gerek yok
        if (accessToken) return;

        const data = await refreshAccess(); // cookie ile
        if (cancelled) return;

        dispatch(setAccess({ token: data.access_token, exp: data.exp }));
      } catch (e) {
        // cookie yoksa / refresh başarısızsa -> login ekranında kalırız
        dispatch(logoutAction());
      } finally {
        if (!cancelled) setBooting(false);
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap: sadece mount'ta, cookie ile refresh
  }, []);

  /**
   * accessToken geldiğinde /auth/me çağırır ve redux'a setMe yapar.
   */
  useEffect(() => {
    let cancelled = false;

    async function loadMe() {
      if (!accessToken) return;
      if (me) return; // zaten set edilmişse tekrar çekme

      try {
        const meData = await getMe(accessToken);
        if (cancelled) return;
        dispatch(setMe(meData));
      } catch (e) {
        dispatch(logoutAction());
        navigate("/", { replace: true });
      }
    }

    loadMe();

    return () => {
      cancelled = true;
    };
  }, [accessToken, me, dispatch, navigate]);

  // Token bitmeden uyarı aç
  const onWarning = useCallback(() => {
    if (status !== "authenticated") return;
    setDialogOpen(true);
  }, [status]);

  // Token süresi dolunca kesin logout
  const onExpire = useCallback(() => {
    doLogout();
  }, [doLogout]);

  // boot bitmeden timer kurma 
  useSessionTimers(booting ? null : accessExp, WARNING_BEFORE_MS, onWarning, onExpire);

  // Uzat butonu
  const handleExtend = useCallback(async () => {
    if (!(status === "authenticated" && extensionsLeft > 0)) {
      doLogout();
      return;
    }

    try {
      const data = await refreshAccess(); // cookie ile
      dispatch(setAccess({ token: data.access_token, exp: data.exp }));
      dispatch(consumeExtension()); // 1 hakkı düş
      setDialogOpen(false);

    } catch (e) {
      doLogout();
    }
  }, [status, extensionsLeft, dispatch, doLogout]);

  if (booting) {
    return <div style={{ padding: 24 }}>{t("nav.loading")}</div>;
  }

  const isAuthenticated = !!accessToken;
  const mustChangePassword = Boolean(me?.must_change_password);

  if (isAuthenticated && mustChangePassword) {
    return (
      <>
        <LanguageSwitcher floating />
        <SessionExpiryDialog
          open={dialogOpen}
          canExtend={canExtend}
          onExtend={handleExtend}
          onLogout={doLogout}
        />
        <Routes>
          <Route path="/change-password-required" element={<FirstLoginPassword />} />
          <Route path="*" element={<Navigate to="/change-password-required" replace />} />
        </Routes>
      </>
    );
  }

  // The role requires two-factor and it is not set up yet: only the setup screen is reachable (the server refuses the rest).
  if (isAuthenticated && me?.two_factor_setup_required) {
    return (
      <>
        <LanguageSwitcher floating />
        <SessionExpiryDialog
          open={dialogOpen}
          canExtend={canExtend}
          onExtend={handleExtend}
          onLogout={doLogout}
        />
        <Routes>
          <Route path="/two-factor-setup" element={<TwoFactorSettings forced />} />
          <Route path="*" element={<Navigate to="/two-factor-setup" replace />} />
        </Routes>
      </>
    );
  }

  // Login sayfasi icin layout gosterme
  if (!isAuthenticated) {
    return (
      <>
        <LanguageSwitcher floating />
        <SessionExpiryDialog
          open={dialogOpen}
          canExtend={canExtend}
          onExtend={handleExtend}
          onLogout={doLogout}
        />
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/forgot-password" element={<Forgot />} />  
        <Route path="/reset-password/:reset_code" element={<ResetPassword />} />  
        <Route path="*" element={<Login />} />
      </Routes>
      </>
    );
  }

  return (
    <>
      <SessionExpiryDialog
        open={dialogOpen}
        canExtend={canExtend}
        onExtend={handleExtend}
        onLogout={doLogout}
      />

      <MainLayout>
        <Routes>
          <Route path="/" element={<ProductGate product="iot"><Home /></ProductGate>} />
          <Route path="/home" element={<ProductGate product="iot"><Home /></ProductGate>} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/edges" element={<ProductGate product="iot"><EdgeExplorer /></ProductGate>} />
          <Route path="/edges/legacy" element={<ProductGate product="iot"><EdgeList /></ProductGate>} />
          <Route path="/device/:serial" element={<ProductGate product="iot"><DeviceLookup /></ProductGate>} />
          <Route path="/edges/:edgeDbId" element={<ProductGate product="iot"><EdgeDetail /></ProductGate>} />
          <Route path="/edge-transfers" element={<ProductGate product="iot"><EdgeTransfers /></ProductGate>} />
          <Route path="/tenants" element={<TenantList />} />
          <Route path="/alarms" element={<ProductGate product="iot"><AlarmList /></ProductGate>} />
          <Route path="/vpn-users" element={<ProductGate product="iot"><VpnUsers /></ProductGate>} />
          <Route path="/marketplace" element={<ProductGate product="iot"><Marketplace /></ProductGate>} />
          <Route path="/marketplace/history" element={<ProductGate product="iot"><MarketplaceHistory /></ProductGate>} />
          <Route path="/ota-management" element={<Navigate to="/ota-management/releases" replace />} />
          <Route path="/ota-management/:tab" element={<OtaManagement />} />
          <Route path="/security" element={<SecurityCenter />} />
          <Route path="/machine-sbom" element={<MachineSbom />} />
          <Route path="/licenses" element={<LicenseManagement />} />
          <Route path="/ssh-access" element={<SshAccess />} />
          <Route path="/ai-settings" element={<AiSettings />} />
          <Route path="/rca" element={<ProductGate product="iot"><RcaAssistant /></ProductGate>} />

      </Routes>
      </MainLayout>
    </>
  );
}

/**
 * App:
 * - Provider + Router sarmalı
 * - AppShell Router içinde olmalı (useNavigate kullandığı için)
 */
export default function App() {
  return (
    <Provider store={store}>
      <Router basename={base_name ? `/${base_name}` : ""}>
        <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} newestOnTop />
        <AppShell />
      </Router>
    </Provider>
  );
}
